import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Sparkles, Cpu, Zap, TrendingUp, MessageSquare, Mail, Phone, Calendar, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIFollowUpConfig = () => {
  const { getAuthHeader, admin } = useAuth();
  const [loading, setLoading] = useState(false);

  return (
    <div className="ai-followup-config">
      <div className="scoring-header">
        <div>
          <h1>
            <Sparkles size={28} /> AI Follow-Up Assistant
          </h1>
          <p className="text-muted-foreground">Leverage AI to generate intelligent follow-up suggestions for leads</p>
        </div>
      </div>

      {/* Feature Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-3">
              <MessageSquare size={24} className="text-white" />
            </div>
            <CardTitle className="text-lg">Smart Message Generation</CardTitle>
            <CardDescription>
              AI analyzes lead context and generates personalized follow-up messages for WhatsApp, Email, or SMS.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-green-600 font-semibold">
              <CheckCircle size={16} />
              <span>Available in Lead Management</span>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-3">
              <Cpu size={24} className="text-white" />
            </div>
            <CardTitle className="text-lg">Contextual Analysis</CardTitle>
            <CardDescription>
              Considers lead stage, days since last contact, lead score, and engagement history to craft relevant messages.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-green-600 font-semibold">
              <CheckCircle size={16} />
              <span>Context-Aware AI</span>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-3">
              <TrendingUp size={24} className="text-white" />
            </div>
            <CardTitle className="text-lg">Action Recommendations</CardTitle>
            <CardDescription>
              AI suggests the best follow-up action (call, email, WhatsApp) based on lead behavior and preferences.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-green-600 font-semibold">
              <CheckCircle size={16} />
              <span>Intelligent Suggestions</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={24} /> How AI Follow-Up Works
          </CardTitle>
          <CardDescription>Seamlessly integrated into your lead management workflow</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h4 className="font-semibold mb-1">Open a Lead</h4>
                <p className="text-sm text-muted-foreground">Navigate to the Lead Management page and select any lead you want to follow up with.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h4 className="font-semibold mb-1">Request AI Suggestion</h4>
                <p className="text-sm text-muted-foreground">Click on "AI Follow-Up" or similar action button within the lead details view.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h4 className="font-semibold mb-1">Review & Customize</h4>
                <p className="text-sm text-muted-foreground">AI generates a personalized message based on lead context. Review, edit if needed, and send.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h4 className="font-semibold mb-1">Track Engagement</h4>
                <p className="text-sm text-muted-foreground">Monitor response rates and engagement to continuously improve your follow-up strategy.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare size={24} /> Available Channels
          </CardTitle>
          <CardDescription>AI can generate content for multiple communication channels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <Mail size={32} className="text-blue-600 mb-2" />
              <h4 className="font-semibold text-blue-900 mb-1">Email</h4>
              <p className="text-sm text-blue-700">Professional email templates with subject lines and body content</p>
            </div>
            <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
              <MessageSquare size={32} className="text-green-600 mb-2" />
              <h4 className="font-semibold text-green-900 mb-1">WhatsApp</h4>
              <p className="text-sm text-green-700">Concise, conversational messages optimized for WhatsApp</p>
            </div>
            <div className="p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
              <Phone size={32} className="text-purple-600 mb-2" />
              <h4 className="font-semibold text-purple-900 mb-1">Call Script</h4>
              <p className="text-sm text-purple-700">Talking points and conversation starters for phone calls</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card className="mt-6 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-primary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={24} className="text-primary" /> Quick Start Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm font-semibold text-primary">✨ To use AI Follow-Up:</p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Go to <strong>Lead Management</strong> page</li>
              <li>Click on any lead to view details</li>
              <li>Look for the <strong>"AI Follow-Up"</strong> button</li>
              <li>Choose your preferred channel (Email, WhatsApp, or Call)</li>
              <li>Review the AI-generated content</li>
              <li>Send or schedule the follow-up</li>
            </ol>
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> AI suggestions are generated in real-time based on the latest lead data. 
                The system learns from successful interactions to improve future suggestions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIFollowUpConfig;
